import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'

import { Modal } from './Modal'
import { cn } from '@/lib/utils'

interface Identifiable { id: number }

interface Props<T extends Identifiable> {
  value: number | null
  onChange: (item: T) => void
  /** 이미 아는 표시 라벨. 주면 마운트 시 fetchDetail 을 생략(대량 리스트에서 값별
   *  개별 API 호출 → 브라우저 OOM 방지). 값이 바뀌면(선택/외부변경) 갱신됨. */
  initialLabel?: string

  title: string
  triggerPlaceholder: string
  searchPlaceholder: string
  emptyText?: string
  /** 트리거 버튼 앞에 표시할 아이콘 (선택). */
  triggerIcon?: ReactNode

  /** 선택된 id 로부터 아이템 상세 조회. */
  fetchDetail: (id: number) => Promise<T>
  /** 검색어로 목록 조회. */
  fetchList: (search: string) => Promise<T[]>
  /** react-query key 앞부분 (검색어는 자동 append). */
  queryKey: readonly unknown[]

  /** 트리거 버튼에 표시할 라벨. */
  formatLabel: (item: T) => string
  /** 결과 행 렌더 (버튼 내부). */
  renderRow: (item: T) => ReactNode
}

/** 3종 picker(Partner/Item/Group) 공통 골격.
 *  - 트리거 버튼 → 모달 → 검색 + 결과 리스트 → 클릭으로 선택.
 *  - 도메인별 차이는 props (endpoint / 라벨 / row 렌더)로 주입.
 */
export function ListPickerModal<T extends Identifiable>({
  value, onChange, initialLabel,
  title, triggerPlaceholder, searchPlaceholder, emptyText = '검색 결과 없음',
  triggerIcon,
  fetchDetail, fetchList, queryKey,
  formatLabel, renderRow,
}: Props<T>) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [label, setLabel] = useState(initialLabel ?? '')
  const [activeIdx, setActiveIdx] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)
  // 라벨을 이미 확보한 value (선택 또는 initialLabel). 이 value 면 fetchDetail 생략.
  const resolvedValue = useRef<number | null>(initialLabel ? value : null)

  useEffect(() => {
    if (value == null) { setLabel(''); resolvedValue.current = null; return }
    if (resolvedValue.current === value) return   // 이미 라벨 있음 → 개별 fetch 생략
    let cancelled = false
    fetchDetail(value)
      .then((item) => { if (!cancelled) { setLabel(formatLabel(item)); resolvedValue.current = value } })
      .catch(() => { /* ignore */ })
    return () => { cancelled = true }
    // fetchDetail/formatLabel 은 상위에서 새 참조로 오지만 value 만이 실질 dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // 선택 시 라벨을 즉시 반영(재-fetch 불필요) + resolvedValue 갱신.
  function selectItem(item: T) {
    setLabel(formatLabel(item))
    resolvedValue.current = item.id
    onChange(item)
    setOpen(false)
  }

  const { data: results = [] } = useQuery({
    queryKey: [...queryKey, search],
    queryFn: () => fetchList(search),
    enabled: open,
    staleTime: 30_000,
  })

  // 검색어 바뀌거나 열릴 때 하이라이트 초기화
  useEffect(() => { setActiveIdx(0) }, [search, open])

  // 하이라이트된 행을 화면에 스크롤
  useEffect(() => {
    if (!open) return
    const el = listRef.current?.querySelector<HTMLElement>(
      `[data-picker-idx="${activeIdx}"]`,
    )
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx, open, results.length])

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(i + 1, Math.max(results.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Home') {
      e.preventDefault(); setActiveIdx(0)
    } else if (e.key === 'End') {
      e.preventDefault(); setActiveIdx(Math.max(results.length - 1, 0))
    } else if (e.key === 'Enter') {
      const item = results[activeIdx]
      if (item) { e.preventDefault(); selectItem(item) }
    }
    // Escape 는 Modal 컴포넌트의 window 리스너가 스택 기반으로 처리 (중첩 시 최상단만 닫음).
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-left bg-white hover:bg-slate-50 inline-flex items-center gap-2"
      >
        {triggerIcon}
        <span className={cn('truncate', label ? 'text-slate-900' : 'text-slate-400')}>
          {label || triggerPlaceholder}
        </span>
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title={title}>
        <div className="relative mb-3">
          <input
            type="text" autoFocus value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={searchPlaceholder}
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
        </div>
        <div ref={listRef} className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg">
          {results.map((item, i) => (
            <button
              key={item.id}
              type="button"
              data-picker-idx={i}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => selectItem(item)}
              className={cn(
                'w-full text-left px-3 py-2 border-b border-slate-100 text-sm',
                i === activeIdx
                  ? 'bg-blue-100'
                  : item.id === value
                    ? 'bg-blue-50'
                    : 'hover:bg-blue-50',
              )}
            >
              {renderRow(item)}
            </button>
          ))}
          {!results.length && (
            <div className="text-center text-slate-400 py-6 text-sm">{emptyText}</div>
          )}
        </div>
        <div className="mt-2 text-[11px] text-slate-400 text-right">
          ↑↓ 이동 · Enter 선택 · Esc 닫기
        </div>
      </Modal>
    </>
  )
}
